import {
  Button,
  Group,
  Modal,
  Select,
  Text,
  TextInput,
  Title,
  useMantineTheme
} from "@mantine/core"
import React, { useEffect, useState } from "react"
import * as yup from "yup"

import { yupResolver } from "@hookform/resolvers/yup"
import { useDisclosure } from "@mantine/hooks"
import { IconCheck, IconPlus } from "@tabler/icons-react"
import { debounce } from "lodash"
import { Controller, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { useParams } from "react-router-dom"
import { CollectionQuery } from "../../../models/collection.model"
import { ParkingActivity } from "../../../models/parking-activity.model"
import { ParkingLot } from "../../../models/parking-lot.model"
import { useCurrentUser } from "../../../shared/hooks/current-user.hook"
import { useLazyGetParkingLotsQuery } from "../../parking-lot/store/parking-lot.query"
import { useCalculateActivityPriceMutation, useCreateParkingActivityMutation } from "../store/parking-activity.query"

const PortalParkingActivityPage: React.FC = () => {
  const theme = useMantineTheme()
  const { t } = useTranslation()
  const {id} = useParams();

  const [completeOpened, { open: openComplete, close: closeComplete }] =
    useDisclosure(false)
  const [addOpened, { open: openAdd, close: closeAdd }] = useDisclosure(false)
  const [licensePlate, setLicensePlate] = useState("")
  const [phone, setPhone] = useState<string>("")
  const [userInfo, currentUserId, ownerInfo, tenantInfo] = useCurrentUser()
  const [parkingLotSearch, setParkingLotSearch] = useState<string | null>(null)

  const [parkingLotCollection, setParkingLotCollection] =
    useState<CollectionQuery>({
      top: 10,
      skip: 0,
      includes: ["property"],
    })
  const [createParkingActivity, createParkingActivityResponse] =
    useCreateParkingActivityMutation()
  const [completeParkingActivity, completeParkingActivityResponse] = useCalculateActivityPriceMutation()
  const [getParkingLots, parkingLots] = useLazyGetParkingLotsQuery()

  let schema: any = yup.object().shape({
    licensePlate: yup
      .string()
      .min(2, t("nameMin", { count: 2 }))
      .max(100, t("nameMax", { count: 100 }))
      .required(t("requiredValidation", { field: t("buyerName") })),
    phoneNumber: yup
      .string()
      .min(2, t("nameMin", { count: 2 }))
      .max(100, t("nameMax", { count: 100 }))
      .required(t("requiredValidation", { field: t("buyerName") })),

    parkingLotId: yup
      .string()
      .required(
        t("requiredValidation", { field: t("parkingLot", { count: 1 }) }),
      ),
  })
  let cSchema: any = yup.object().shape({
    licensePlate: yup
      .string()
      .nullable(),
      // .min(2, t("nameMin", { count: 2 }))
      // .max(100, t("nameMax", { count: 100 })),
    phoneNumber: yup
      .string()
      .nullable()
      // .min(2, t("nameMin", { count: 2 }))
      // .max(100, t("nameMax", { count: 100 })),

  })

  const {
    register,
    control,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
  } = useForm<ParkingActivity>({
    resolver: yupResolver(schema),
    mode: "all",
  })
  const {
    register: cRegister,
    control: cControl,
    handleSubmit: cHandleSubmit,
    reset: cReset,
    formState: { errors:cErrors },
  } = useForm<ParkingActivity>({
    resolver: yupResolver(cSchema),
    mode: "all",
  })

  const parkingLotWatch = watch("parkingLotId")

  useEffect(() => {
    if (userInfo?.role?.key == "super_admin") {
      getParkingLots(parkingLotCollection)
    } else if (userInfo?.role?.key == "tenant") {
      getParkingLots({
        ...parkingLotCollection,
        filter: [
          [
            {
              field: "property.ownerId",
              value: tenantInfo?.ownerId,
              operator: "=",
            },
          ],
        ],
      })
    } else {
      getParkingLots({
        ...parkingLotCollection,
        filter: [
          [
            {
              field: "property.ownerId",
              value: id,
              operator: "=",
            },
          ],
        ],
      })
    }
  }, [parkingLotWatch])

  useEffect(() => {
    if (parkingLotSearch) {
      if (userInfo?.role?.key == "super_admin") {
        getParkingLots({
          ...parkingLotCollection,
          filter: [
            [{ field: "name", operator: "like", value: parkingLotSearch }],
          ],
        })
      } else if (userInfo?.role?.key == "tenant") {
        getParkingLots({
          ...parkingLotCollection,
          filter: [
            [{ field: "name", operator: "like", value: parkingLotSearch }],
            [
              {
                field: "property.ownerId",
                operator: "=",
                value: tenantInfo?.ownerId,
              },
            ],
          ],
        })
      } else {
        getParkingLots({
          ...parkingLotCollection,
          filter: [
            [{ field: "name", operator: "like", value: parkingLotSearch }],
            [
              {
                field: "property.ownerId",
                operator: "=",
                value: id,
              },
            ],
          ],
        })
      }
    }
  }, [parkingLotSearch])
  function _parseParkingLots() {
    let data: { label: string; value: string }[] = []

    parkingLots?.data?.data?.map((parkingLot: ParkingLot) => {
      data.push({
        label: `${parkingLot?.name as string} (${
          parkingLot?.property?.name as string
        })`,
        value: parkingLot?.id as string,
      })
    })
    return data
  }

  function onSubmit(data: any) {
    createParkingActivity(data)
    reset()
    closeAdd()
  }
  function cOnSubmit(data: any) {
    completeParkingActivity(data)
    cReset()
    closeComplete()
  }
  return (
    <div style={{ padding: theme.spacing.md }}>
      <Title order={2} mb="sm">
        Parking Activities
      </Title>
      <Text color="dimmed" mb="md">
        Manage your parking activities with the actions below
      </Text>

      <Group>
        {/* Complete Activity Button */}
        <Button
          leftIcon={<IconCheck size={16} />}
          color="green"
          onClick={openComplete}
        >
          Complete Activity
        </Button>

        {/* Add Activity Button */}
        <Button
          leftIcon={<IconPlus size={16} />}
          color={theme.primaryColor}
          onClick={openAdd}
        >
          Add New Activity
        </Button>
      </Group>

      {/* Complete Activity Modal */}
      <Modal
        opened={completeOpened}
        onClose={closeComplete}
        title="Complete Activity"
        centered
        withCloseButton={false}
      >
        <Text mb="md">
          Are you sure you want to mark this activity as complete?
        </Text>
         <form onSubmit={cHandleSubmit(cOnSubmit, (e) => console.log(e))} className="flex flex-col gap-4">
          <TextInput
            classNames={{ label: "dark:text-white" }}
            className="w-full"
            error={cErrors.phoneNumber && `${cErrors?.phoneNumber?.message}`}
            // withAsterisk
            label={t("phoneNumber")}
            placeholder={t("phoneNumber")}
            {...cRegister("phoneNumber")}
          />
          <TextInput
            classNames={{ label: "dark:text-white" }}
            className="w-full"
            error={cErrors.licensePlate && `${cErrors?.licensePlate?.message}`}
            // withAsterisk
            label={t("plateNumber")}
            placeholder={t("plateNumber")}
            {...cRegister("licensePlate")}
          />
          <Group position="right" className="mt-4">
            <Button variant="default" onClick={closeAdd}>
              Cancel
            </Button>
            <Button
              color={theme.primaryColor}
              // onClick={handleAdd}
              type="submit"
              disabled={
                
                createParkingActivityResponse.isLoading
              }
            >
              Complete Activity
            </Button>
          </Group>
        </form>

        
      </Modal>

      {/* Add Activity Modal */}
      <Modal
        opened={addOpened}
        onClose={closeAdd}
        title="Add New Activity"
        centered
        withCloseButton={false}
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <TextInput
            classNames={{ label: "dark:text-white" }}
            className="w-full"
            error={errors.phoneNumber && `${errors?.phoneNumber?.message}`}
            withAsterisk
            label={t("phoneNumber")}
            placeholder={t("phoneNumber")}
            {...register("phoneNumber")}
          />
          <TextInput
            classNames={{ label: "dark:text-white" }}
            className="w-full"
            error={errors.licensePlate && `${errors?.licensePlate?.message}`}
            withAsterisk
            label={t("plateNumber")}
            placeholder={t("plateNumber")}
            {...register("licensePlate")}
          />
          <Controller
            control={control}
            name="parkingLotId"
            render={({ field }) => (
              <Select
                classNames={{ label: "dark:text-white" }}
                className="w-full"
                onKeyDown={debounce(
                  (event: any) => setParkingLotSearch(event?.target?.value),
                  1000,
                )}
                searchable
                clearable
                withAsterisk
                nothingFound={<p>{t("parkingLotNotFound")}</p>}
                error={
                  errors.parkingLotId && `${errors?.parkingLotId?.message}`
                }
                label={t("parkingLot", { count: 1 })}
                placeholder={t("parkingLot", { count: 1 })}
                data={_parseParkingLots()}
                {...field}
              />
            )}
          />
          <Group position="right" className="mt-4">
            <Button variant="default" onClick={closeAdd}>
              Cancel
            </Button>
            <Button
              color={theme.primaryColor}
              // onClick={handleAdd}
              type="submit"
              disabled={
                
                createParkingActivityResponse.isLoading
              }
            >
              Add Activity
            </Button>
          </Group>
        </form>
      </Modal>
    </div>
  )
}

export default PortalParkingActivityPage
